import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { FolderIcon, PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { InfoPopover } from '@/shared/ui';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import type { FolderIdentityProps } from './FolderIdentity.types';

export function FolderIdentity({ title, description, canEdit, onEdit, info }: FolderIdentityProps) {
  const { t } = useTranslation('knowledge');
  const editLabel = t('guidelines.folderIdentity.editFolder');
  const unknown = t('guidelines.guidelineFolderInfoPopover.unknown');

  return (
    <div className="flex items-start justify-between gap-md">
      <div className="flex min-w-0 items-start gap-md">
        <div className="flex size-14 shrink-0 items-center justify-center rounded-sm bg-surface">
          <FolderIcon className="size-6 text-foreground" />
        </div>
        <div className="flex min-w-0 flex-col gap-3xs">
          <h2 className="truncate text-heading-lg font-medium tracking-tight">{title}</h2>
          {description && (
            <p className="truncate text-body-md text-foreground-subtle">{description}</p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-xs">
        <InfoPopover
          label={t('guidelines.guidelineFolderInfoPopover.folderInfo')}
          rows={[
            {
              label: t('guidelines.guidelineFolderInfoPopover.created'),
              value: formatRelativeDate(info.created) || unknown,
            },
            {
              label: t('guidelines.guidelineFolderInfoPopover.lastModified'),
              value: formatRelativeDate(info.updated) || unknown,
            },
          ]}
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  variant="ghost"
                  size="icon-lg"
                  disabled={!canEdit}
                  onClick={onEdit}
                  aria-label={editLabel}
                >
                  <PencilIcon />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{editLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}

FolderIdentity.displayName = 'FolderIdentity';
